import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Alert, Box, Button, CircularProgress, Container, Paper, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AvanzuHeader } from '../../components/AvanzuHeader';

const VerifyEmailSuccess: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  const [alreadyVerified, setAlreadyVerified] = useState(false);
  const hasAttemptedVerification = useRef(false);

  useEffect(() => {
    let mounted = true;

    const verifyEmail = async () => {
      if (!token) {
        if (mounted) {
          setStatus('error');
          setMessage('Invalid verification link');
        }
        return;
      }

      // Wait for auth to finish loading and ensure we have a user
      if (loading) {
        return;
      }
      // Prevent duplicate API calls
      if (hasAttemptedVerification.current) {
        return;
      }

      hasAttemptedVerification.current = true;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/verify-email/${token}`);
        const data = await response.json();

        if (!mounted) return; // Prevent state update if component unmounted

        if (data.success) {
          setStatus('success');
          setMessage(data.message);
          setAlreadyVerified(data.alreadyVerified || false);
        } else {
          setStatus('error');
          setMessage(data.error || 'Email verification failed');
        }
      } catch (error) {
        console.error('Email verification error:', error);
        if (mounted) {
          setStatus('error');
          setMessage('Failed to verify email. Please try again.');
        }
      }
    };

    verifyEmail();

    return () => {
      mounted = false;
    };
  }, [token, user, loading]);

  const handleContinue = () => {
    navigate('/');
  };

  if (status === 'loading') {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
          }}
        >
          <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
            <CircularProgress sx={{ mb: 3 }} />
            <Typography variant="h5">Verifying your email address...</Typography>
          </Paper>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <AvanzuHeader />
        </Box>
        <Paper elevation={3} sx={{ p: 4, width: '100%', textAlign: 'center' }}>
          <Box sx={{ mb: 3 }}>
            {status === 'success' ? (
              <CheckCircleIcon
                sx={{
                  fontSize: 64,
                  color: alreadyVerified ? 'warning.main' : 'success.main',
                  mb: 2,
                }}
              />
            ) : (
              <ErrorIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
            )}

            <Typography variant="h4" gutterBottom>
              {status === 'success'
                ? alreadyVerified
                  ? 'Already Verified'
                  : 'Email Verified!'
                : 'Verification Failed'}
            </Typography>
          </Box>

          <Alert
            severity={status === 'success' ? (alreadyVerified ? 'info' : 'success') : 'error'}
            sx={{ mb: 3 }}
          >
            {message}
          </Alert>

          {status === 'success' && (
            <Box>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {alreadyVerified
                  ? 'Your email was already verified. You can continue using your account.'
                  : 'Your email has been successfully verified. You now have full access to your Avanzu account.'}
              </Typography>

              <Button
                variant="contained"
                size="large"
                onClick={handleContinue}
                sx={{ minWidth: 200 }}
              >
                Login to your account
              </Button>
            </Box>
          )}

          {status === 'error' && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                The verification link may have expired or been used already.
              </Typography>

              <Button
                variant="outlined"
                onClick={() => navigate('/verify-email-pending')}
                sx={{ mr: 2 }}
              >
                Request New Link
              </Button>

              <Button variant="contained" onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmailSuccess;
